import { BackendNotification } from "../Api/query/Notifications";
import * as notificationApi from "../Api/query/Notifications";

export interface Notification {
  id: string;
  notification_id?: number; // Backend ID
  title: string;
  message: string;
  time: string;
  timestamp: number;
  read: boolean;
  type?: 'info' | 'success' | 'warning' | 'error';
  category?: 'request_approval' | 'request_execution' | 'request_status' | 'system' | 'other' | 'account_expiry' | 'job_failure' | 'security_alert';
  priority?: 'high' | 'medium' | 'low';
  action_url?: string;
  severity?: string;
  event_type?: string;
  event_subtype?: string;
}

export interface NotificationPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  requestApprovals: boolean;
  requestExecutions: boolean;
  weeklyDigest: boolean;
  accountExpiry: boolean;
  jobFailure: boolean;
  securityAlert: boolean;
}

const NOTIFICATIONS_STORAGE_KEY = 'entitlex_notifications';
const PREFERENCES_STORAGE_KEY = 'entitlex_notification_preferences';

// Default notification preferences
const DEFAULT_PREFERENCES: NotificationPreferences = {
  emailNotifications: true,
  pushNotifications: true,
  requestApprovals: true,
  requestExecutions: true,
  weeklyDigest: false,
  accountExpiry: true,
  jobFailure: true,
  securityAlert: true,
};

/**
 * Convert backend notification to frontend format
 */
export const convertBackendNotification = (backendNotif: BackendNotification): Notification => {
  // Map severity to type
  const severityToType: Record<string, 'info' | 'success' | 'warning' | 'error'> = {
    'Info': 'info',
    'Success': 'success',
    'Warning': 'warning',
    'Error': 'error',
  };

  // Map priority
  const priorityMap: Record<string, 'high' | 'medium' | 'low'> = {
    'High': 'high',
    'Medium': 'medium',
    'Normal': 'medium',
    'Low': 'low',
  };

  // Map category
  const categoryMap: Record<string, Notification['category']> = {
    'Workflow': 'request_status',
    'Request': 'request_status',
    'Security': 'security_alert',
    'System': 'system',
  };

  // Determine category from event_type
  let category: Notification['category'] = categoryMap[backendNotif.category] || 'other';
  if (backendNotif.event_type === 'Request') {
    if (backendNotif.event_subtype?.includes('Approval') || backendNotif.event_subtype === 'Submitted') {
      category = 'request_approval';
    } else if (backendNotif.event_subtype === 'Executed') {
      category = 'request_execution';
    } else if (backendNotif.event_subtype === 'Approved' || backendNotif.event_subtype === 'Denied') {
      category = 'request_status';
    }
  }

  const timestamp = new Date(backendNotif.created_date).getTime();
  // Check if notification is read - status can be 'New', 'Sent', 'Read', 'Acknowledged'
  // Only 'Read' and 'Acknowledged' are considered read
  const isRead = backendNotif.status === 'Read' || backendNotif.status === 'Acknowledged';
  // Also check if read_date exists
  const hasReadDate = !!backendNotif.read_date;

  // Format time - use actual date/time for better clarity
  const timeStr = formatNotificationDateTime(backendNotif.created_date);

  return {
    id: `notif_${backendNotif.notification_id}`,
    notification_id: backendNotif.notification_id,
    title: backendNotif.title,
    message: backendNotif.message,
    time: timeStr,
    timestamp,
    read: isRead || hasReadDate, // Mark as read if status is Read/Acknowledged OR has read_date
    type: severityToType[backendNotif.severity] || 'info',
    category,
    priority: priorityMap[backendNotif.priority] || 'medium',
    action_url: backendNotif.action_url,
    severity: backendNotif.severity,
    event_type: backendNotif.event_type,
    event_subtype: backendNotif.event_subtype,
  };
};

/**
 * Fetch notifications from backend
 * By default, fetches only unread notifications (status != 'Read' && status != 'Acknowledged')
 */
export const fetchNotificationsFromBackend = async (
  filters?: { status?: string; event_type?: string; category?: string },
  pagination?: { page?: number; limit?: number },
  showOnlyUnread: boolean = true
): Promise<{ notifications: Notification[]; unreadCount: number }> => {
  try {
    // If showOnlyUnread is true, don't filter by status - get all and filter client-side
    // Or we can fetch with status filter to get only unread
    const fetchFilters = { ...filters };
    
    // If we want only unread, we'll filter client-side after fetching
    // This ensures we get accurate unread count from backend
    const response = await notificationApi.getUserNotifications(fetchFilters, pagination);
    
    // Convert all notifications
    let allNotifications = response.notifications.map(convertBackendNotification);
    
    // Filter to show only unread if requested
    if (showOnlyUnread) {
      allNotifications = allNotifications.filter(n => !n.read);
    }
    
    // Cache only unread notifications (or all if showOnlyUnread is false)
    saveNotifications(allNotifications);
    
    return {
      notifications: allNotifications,
      unreadCount: response.unread_count,
    };
  } catch (error) {
    console.error('Error fetching notifications from backend:', error);
    // Fallback to localStorage - but only unread ones
    const cached = loadNotificationsFromCache();
    const unreadCached = showOnlyUnread ? cached.filter(n => !n.read) : cached;
    return {
      notifications: unreadCached,
      unreadCount: unreadCached.filter(n => !n.read).length,
    };
  }
};

/**
 * Load notifications from localStorage cache
 */
export const loadNotificationsFromCache = (): Notification[] => {
  try {
    const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
    if (stored) {
      const notifications = JSON.parse(stored);
      // Filter out notifications older than 30 days
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      return notifications.filter((n: Notification) => n.timestamp > thirtyDaysAgo);
    }
  } catch (error) {
    console.error('Error loading notifications from cache:', error);
  }
  return [];
};

/**
 * Load notifications (backward compatibility - now uses backend)
 */
export const loadNotifications = (): Notification[] => {
  return loadNotificationsFromCache();
};

// Save notifications to localStorage
export const saveNotifications = (notifications: Notification[]): void => {
  try {
    localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(notifications));
  } catch (error) {
    console.error('Error saving notifications:', error);
  }
};

// Load notification preferences from localStorage
export const loadNotificationPreferences = (): NotificationPreferences => {
  try {
    const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Error loading notification preferences:', error);
  }
  return DEFAULT_PREFERENCES;
};

// Save notification preferences to localStorage
export const saveNotificationPreferences = (preferences: NotificationPreferences): void => {
  try {
    localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.error('Error saving notification preferences:', error);
  }
};

// Format time ago (relative time for recent notifications)
export const formatTimeAgo = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  // For notifications older than 24 hours, show actual date and time
  if (days > 1) {
    return formatNotificationDateTime(new Date(timestamp));
  } else if (days === 1) {
    return 'Yesterday';
  } else if (hours > 0) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  } else if (minutes > 0) {
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  } else {
    return 'Just now';
  }
};

// Format date and time for notifications (shows actual date/time in user's timezone)
export const formatNotificationDateTime = (date: Date | string): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const notificationDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
    
    // Check if notification is from today
    const isToday = notificationDate.getTime() === today.getTime();
    
    // Check if notification is from yesterday
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = notificationDate.getTime() === yesterday.getTime();
    
    // Get user's locale from browser
    const locale = navigator.language || 'en-US';
    
    // Format time
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    const timeStr = dateObj.toLocaleTimeString(locale, timeOptions);
    
    if (isToday) {
      return `Today at ${timeStr}`;
    } else if (isYesterday) {
      return `Yesterday at ${timeStr}`;
    } else {
      // Format date and time
      const dateOptions: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        year: dateObj.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      };
      const dateStr = dateObj.toLocaleDateString(locale, dateOptions);
      return `${dateStr} at ${timeStr}`;
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

// Create a new notification
export const createNotification = (
  title: string,
  message: string,
  type: Notification['type'] = 'info',
  category: Notification['category'] = 'other',
  priority: Notification['priority'] = 'medium'
): Notification => {
  const timestamp = Date.now();
  return {
    id: `notif_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
    title,
    message,
    time: formatTimeAgo(timestamp),
    timestamp,
    read: false,
    type,
    category,
    priority,
  };
};

// Sort notifications by priority, then unread status, then timestamp
export const sortNotificationsByPriority = (notifications: Notification[]): Notification[] => {
  const priorityOrder = { high: 3, medium: 2, low: 1 };
  
  return [...notifications].sort((a, b) => {
    // First sort by priority (high first)
    const aPriority = priorityOrder[a.priority || 'medium'];
    const bPriority = priorityOrder[b.priority || 'medium'];
    if (aPriority !== bPriority) {
      return bPriority - aPriority;
    }
    
    // Then by unread status (unread first)
    if (a.read !== b.read) {
      return a.read ? 1 : -1;
    }
    
    // Finally by timestamp (newest first)
    return b.timestamp - a.timestamp;
  });
};

// Add notification to storage (from WebSocket or local creation)
export const addNotification = (notification: Notification): void => {
  const notifications = loadNotificationsFromCache();
  // Check if notification already exists (by notification_id or id)
  const exists = notification.notification_id 
    ? notifications.some(n => n.notification_id === notification.notification_id)
    : notifications.some(n => n.id === notification.id);
  
  if (!exists) {
    notifications.unshift(notification); // Add to beginning
    // Keep only last 100 notifications
    const limited = notifications.slice(0, 100);
    saveNotifications(limited);
    
    // Trigger custom event for UI updates
    window.dispatchEvent(new CustomEvent('notificationAdded', { detail: notification }));
  }
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  // Extract backend notification_id if available
  const notifications = loadNotificationsFromCache();
  const notification = notifications.find(n => n.id === notificationId);
  
  if (notification?.notification_id) {
    try {
      await notificationApi.markNotificationAsRead(notification.notification_id);
    } catch (error) {
      console.error('Error marking notification as read on backend:', error);
    }
  }
  
  // Update local cache
  const updated = notifications.map(n =>
    n.id === notificationId ? { ...n, read: true } : n
  );
  saveNotifications(updated);
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async (): Promise<void> => {
  const notifications = loadNotificationsFromCache();
  
  // Mark all unread notifications on backend
  const unreadNotifications = notifications.filter(n => !n.read && n.notification_id);
  await Promise.allSettled(
    unreadNotifications.map(n => 
      notificationApi.markNotificationAsRead(n.notification_id!)
    )
  );
  
  // Update local cache
  const updated = notifications.map(n => ({ ...n, read: true }));
  saveNotifications(updated);
};

// Delete notification
export const deleteNotification = (notificationId: string): void => {
  const notifications = loadNotifications();
  const updated = notifications.filter(n => n.id !== notificationId);
  saveNotifications(updated);
};

// Check if notification should be shown based on preferences
export const shouldShowNotification = (
  notification: Notification,
  preferences: NotificationPreferences
): boolean => {
  // Always show if no category
  if (!notification.category) return true;

  switch (notification.category) {
    case 'request_approval':
      return preferences.requestApprovals;
    case 'request_execution':
      return preferences.requestExecutions;
    case 'account_expiry':
      return preferences.accountExpiry;
    case 'job_failure':
      return preferences.jobFailure;
    case 'security_alert':
      return preferences.securityAlert;
    default:
      return true;
  }
};

// Request browser notification permission
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

// Show browser notification
export const showBrowserNotification = (
  notification: Notification,
  preferences: NotificationPreferences
): void => {
  if (!preferences.pushNotifications) {
    return;
  }

  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(notification.title, {
      body: notification.message,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: notification.id,
    });
  }
};

// Helper function to create and add notification with preferences check
export const notify = (
  title: string,
  message: string,
  type: Notification['type'] = 'info',
  category: Notification['category'] = 'other',
  preferences?: NotificationPreferences,
  priority: Notification['priority'] = 'medium'
): void => {
  const notification = createNotification(title, message, type, category, priority);
  
  // Check if we should show this notification
  if (preferences && !shouldShowNotification(notification, preferences)) {
    return;
  }
  
  // Add to storage
  addNotification(notification);
  
  // Show browser notification if enabled
  if (preferences) {
    showBrowserNotification(notification, preferences);
  }
  
  // Trigger custom event for UI updates
  window.dispatchEvent(new CustomEvent('notificationAdded', { detail: notification }));
};

// Convenience functions for common notification types
export const notifyRequestApproval = (
  requestId: string | number,
  requesterName?: string,
  preferences?: NotificationPreferences,
  priority: Notification['priority'] = 'high'
): void => {
  notify(
    'New Request Requires Approval',
    `Access request ${requestId}${requesterName ? ` from ${requesterName}` : ''} requires your approval`,
    'info',
    'request_approval',
    preferences,
    priority
  );
};

export const notifyRequestExecuted = (
  requestId: string | number,
  success: boolean = true,
  preferences?: NotificationPreferences,
  priority?: Notification['priority']
): void => {
  const defaultPriority: Notification['priority'] = success ? 'medium' : 'high';
  notify(
    success ? 'Request Executed Successfully' : 'Request Execution Failed',
    `Access request ${requestId} has been ${success ? 'executed successfully' : 'failed'}`,
    success ? 'success' : 'error',
    'request_execution',
    preferences,
    priority || defaultPriority
  );
};

export const notifyRequestStatusChange = (
  requestId: string | number,
  status: string,
  preferences?: NotificationPreferences,
  priority?: Notification['priority']
): void => {
  const statusMessages: Record<string, { title: string; type: Notification['type']; priority: Notification['priority'] }> = {
    'approved': { title: 'Request Approved', type: 'success', priority: 'medium' },
    'rejected': { title: 'Request Rejected', type: 'warning', priority: 'high' },
    'cancelled': { title: 'Request Cancelled', type: 'info', priority: 'low' },
    'pending': { title: 'Request Pending', type: 'info', priority: 'medium' },
  };
  
  const statusInfo = statusMessages[status.toLowerCase()] || { title: 'Request Status Changed', type: 'info', priority: 'medium' };
  
  notify(
    statusInfo.title,
    `Access request ${requestId} has been ${status.toLowerCase()}`,
    statusInfo.type,
    'request_status',
    preferences,
    priority || statusInfo.priority
  );
};

export const notifyAccountExpiry = (
  accountName: string,
  daysUntilExpiry: number,
  preferences?: NotificationPreferences
): void => {
  const priority: Notification['priority'] = daysUntilExpiry <= 3 ? 'high' : daysUntilExpiry <= 7 ? 'medium' : 'low';
  notify(
    'Account Expiry Warning',
    `Account ${accountName} will expire in ${daysUntilExpiry} ${daysUntilExpiry === 1 ? 'day' : 'days'}`,
    daysUntilExpiry <= 7 ? 'warning' : 'info',
    'account_expiry',
    preferences,
    priority
  );
};

export const notifyJobFailure = (
  jobName: string,
  errorMessage?: string,
  preferences?: NotificationPreferences,
  priority: Notification['priority'] = 'high'
): void => {
  notify(
    'Job Failure',
    `Background job "${jobName}" has failed${errorMessage ? `: ${errorMessage}` : ''}`,
    'error',
    'job_failure',
    preferences,
    priority
  );
};

export const notifySecurityAlert = (
  title: string,
  message: string,
  severity: 'info' | 'warning' | 'error' = 'warning',
  preferences?: NotificationPreferences,
  priority: Notification['priority'] = 'high'
): void => {
  notify(
    title,
    message,
    severity,
    'security_alert',
    preferences,
    priority
  );
};

